import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import VoiceCall from './Voice_Call';
import VideoCall from './Video_call';
import WebRTCHandler from './handlers/webrtcHandler';
import ZegoCloudHandler from './handlers/zegocloudHandler';
import TwilioHandler from './handlers/twilioHandler';
import { checkCallMediaPermissions } from '../utils/callDeviceChecker';
import { getApiBaseURL } from '../utils/backendUrl';

export default function WebCallManager({ channel, isVideo, isCaller, receiverId, onClose, receiverName, receiverPic }) {
  const [provider, setProvider] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warningMessage, setWarningMessage] = useState(null);

  // 1. Run Pre-Flight Hardware & Permission Checks
  const runPreflightCheck = useCallback(async () => {
    setLoading(true);
    setWarningMessage(null);

    const check = await checkCallMediaPermissions({ isVideo });
    if (!check.ok) {
      setWarningMessage(check.message);
      setLoading(false);
      return false;
    }

    return true;
  }, [isVideo]);

  useEffect(() => {
    async function initCall() {
      const allowed = await runPreflightCheck();
      if (!allowed) return;

      try {
        let token = localStorage.getItem('token') || '';
        if (!token) {
          const localUser = localStorage.getItem('Register_User');
          if (localUser) {
            try {
              token = JSON.parse(localUser)?.token || '';
            } catch (_) {}
          }
        }
        const apiBase = getApiBaseURL();
        const res = await axios.post(`${apiBase}call/token`, {
          channel,
          type: isVideo ? 'video' : 'audio',
          receiverId
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (res.data && res.data.Result === 'true') {
          setProvider(res.data.provider || 'agora');
          setCredentials(res.data);
          setLoading(false);
        } else {
          setError(res.data.ResponseMsg || 'Failed to fetch call credentials.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Error connecting to call server.');
        setLoading(false);
      }
    }

    initCall();
  }, [channel, isVideo, receiverId, runPreflightCheck]);

  // Render Permission / Hardware Warning Overlay
  if (warningMessage) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 text-white">
        <div className="flex max-w-md flex-col items-center gap-4 rounded-3xl bg-gray-900 p-6 text-center shadow-2xl">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/20 text-amber-500">
            <i className="fa fa-exclamation-triangle text-3xl"></i>
          </div>
          <h3 className="text-xl font-bold text-amber-400">Media Permission / Hardware Required</h3>
          <p className="text-sm text-gray-300">{warningMessage}</p>
          <div className="flex gap-3 mt-2">
            <button onClick={runPreflightCheck} className="rounded-xl bg-purple-600 px-5 py-2 font-medium hover:bg-purple-700">
              Grant & Retry
            </button>
            <button onClick={onClose} className="rounded-xl bg-gray-800 px-5 py-2 font-medium hover:bg-gray-700">
              Cancel Call
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 text-white">
        <div className="flex flex-col items-center gap-3 rounded-2xl bg-gray-900 p-6 shadow-2xl">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"></div>
          <p className="text-sm font-medium">Checking devices & connecting call...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 text-white">
        <div className="flex max-w-md flex-col items-center gap-4 rounded-2xl bg-gray-900 p-6 text-center shadow-2xl">
          <h4 className="text-lg font-bold text-red-500">Call Error</h4>
          <p className="text-sm text-gray-300">{error}</p>
          <button onClick={onClose} className="rounded-xl bg-gray-800 px-4 py-2 text-sm font-medium hover:bg-gray-700">
            Close
          </button>
        </div>
      </div>
    );
  }

  // Switch Call Provider Handlers
  if (provider === 'webrtc') {
    return (
      <div className="px-[15px] py-[15px] w-full h-full fixed top-0 left-0 flex items-center justify-center bg-black bg-opacity-60 z-[999]">
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[430px] rounded-[28px] overflow-hidden">
          <WebRTCHandler
            channel={channel}
            isVideo={isVideo}
            isCaller={isCaller}
            receiverId={receiverId}
            receiverName={receiverName}
            receiverPic={receiverPic}
            credentials={credentials?.credentials}
            onClose={onClose}
          />
        </div>
      </div>
    );
  }

  if (provider === 'zegocloud') {
    return (
      <div className="px-[15px] py-[15px] w-full h-full fixed top-0 left-0 flex items-center justify-center bg-black bg-opacity-60 z-[999]">
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[430px] rounded-[28px] overflow-hidden">
          <ZegoCloudHandler
            channel={channel}
            isVideo={isVideo}
            isCaller={isCaller}
            receiverId={receiverId}
            receiverName={receiverName}
            receiverPic={receiverPic}
            credentials={credentials?.credentials}
            token={credentials?.token}
            onClose={onClose}
          />
        </div>
      </div>
    );
  }

  if (provider === 'twilio') {
    return (
      <div className="px-[15px] py-[15px] w-full h-full fixed top-0 left-0 flex items-center justify-center bg-black bg-opacity-60 z-[999]">
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[430px] rounded-[28px] overflow-hidden">
          <TwilioHandler
            channel={channel}
            isVideo={isVideo}
            isCaller={isCaller}
            receiverId={receiverId}
            receiverName={receiverName}
            receiverPic={receiverPic}
            credentials={credentials?.credentials}
            token={credentials?.token}
            onClose={onClose}
          />
        </div>
      </div>
    );
  }

  // Default to Agora Provider Handler
  if (isVideo) {
    return (
      <div className="px-[15px] py-[15px] w-full h-full fixed top-0 left-0 flex items-center justify-center bg-black bg-opacity-60 z-[999]">
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[430px] rounded-[28px] overflow-hidden">
          <VideoCall
            Id={receiverId}
            img={receiverPic}
            name={receiverName}
            channelname={channel}
            credentials={credentials}
            onClose={onClose}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="px-[15px] py-[15px] w-full h-full fixed top-0 left-0 flex items-center justify-center bg-black bg-opacity-60 z-[999]">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[430px] rounded-[28px] overflow-hidden">
        <VoiceCall
          Id={receiverId}
          receiverId={receiverId}
          name={receiverName}
          img={receiverPic}
          channelname={channel}
          credentials={credentials}
          onClose={onClose}
        />
      </div>
    </div>
  );
}
